import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button, Input, Modal, Select } from '@/components/ui';
import {
  listCompanyEmployments,
  listPendingCompanyEmployments,
  startCompanyEmployment,
  type StartEmploymentInput,
} from '@/api/services/employment.service';
import { listHrStructure, type HrStructureKind } from '@/api/services/hr-structure.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { usePermissions } from '@/hooks/usePermissions';

type ReferenceField =
  | 'departmentId'
  | 'teamId'
  | 'positionId'
  | 'jobTitleId'
  | 'gradeId'
  | 'employmentTypeId';

const EMPTY_REFERENCES: Record<ReferenceField, string> = {
  departmentId: '',
  teamId: '',
  positionId: '',
  jobTitleId: '',
  gradeId: '',
  employmentTypeId: '',
};

function todayInputValue(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

/**
 * Starts the company employment of a member added outside candidate hiring.
 * The choices become the initial assignment; later changes go through movements.
 */
export function StartEmploymentModal({
  companyId,
  userId,
  employeeName,
  onClose,
}: {
  companyId: string;
  userId: string;
  employeeName: string;
  onClose: () => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const canReadStructure = can('hr', 'structure');
  const [joinedAt, setJoinedAt] = useState(todayInputValue);
  const [status, setStatus] = useState<StartEmploymentInput['status']>('probation');
  const [references, setReferences] = useState(EMPTY_REFERENCES);
  const [managerId, setManagerId] = useState('');
  const [workLocation, setWorkLocation] = useState<'onsite' | 'remote' | 'hybrid'>('onsite');
  const [formError, setFormError] = useState<string | null>(null);

  const { data: structure } = useQuery({
    queryKey: ['hr-structure', companyId],
    queryFn: () => listHrStructure(companyId),
    enabled: canReadStructure,
  });
  const { data: directory } = useQuery({
    queryKey: ['hr-movements-directory', companyId],
    queryFn: () => listCompanyEmployments(companyId, 1, 100),
  });
  const { data: pendingEmployments } = useQuery({
    queryKey: ['hr-pending-employments', companyId, 'manager-options'],
    queryFn: () => listPendingCompanyEmployments(companyId, 1, 100),
  });

  const structureRows = Array.isArray(structure) ? structure : [];
  const facets = directory?.facets;
  const managerOptions = Array.from(
    new Map(
      [
        ...(Array.isArray(directory?.data) ? directory.data : []),
        ...(Array.isArray(pendingEmployments?.data)
          ? pendingEmployments.data.filter((row) => row.status === 'active')
          : []),
      ]
        .filter((row) => row.userId !== userId && row.employee)
        .map((row) => [
          row.userId,
          {
            value: row.userId,
            label: `${row.employee?.firstName ?? ''} ${row.employee?.lastName ?? ''}`.trim(),
          },
        ])
    ).values()
  );
  // Structure is the complete catalogue; directory facets are the fallback for
  // callers who can start employments but not administer the structure.
  const optionsFor = (
    kind: HrStructureKind,
    fallback?: Array<{ id: string; name: string }>,
    underDepartment = false
  ): Array<{ value: string; label: string }> => {
    const fromStructure = structureRows
      .filter((row) => row.kind === kind && row.active !== false)
      .filter(
        (row) =>
          !underDepartment ||
          !row.parentId ||
          !references.departmentId ||
          row.parentId === references.departmentId
      )
      .map((row) => ({ value: row._id, label: row.name }));
    const list =
      fromStructure.length > 0 || canReadStructure
        ? fromStructure
        : (Array.isArray(fallback) ? fallback : []).map((facet) => ({
            value: facet.id,
            label: facet.name,
          }));
    return [{ value: '', label: 'Not set' }, ...list];
  };

  const setReference = (field: ReferenceField) => (value: string) => {
    setReferences((current) => ({
      ...current,
      [field]: value,
      // Team and position belong to a department; a new department clears them.
      ...(field === 'departmentId' && { teamId: '', positionId: '' }),
    }));
    setFormError(null);
  };

  const start = useMutation({
    mutationFn: (input: StartEmploymentInput) => startCompanyEmployment(companyId, userId, input),
    onSuccess: (result) => {
      toast.success(`Employment started (${result.lifecycleStatus.replaceAll('_', ' ')})`);
      void queryClient.invalidateQueries({ queryKey: ['hr-pending-employments'] });
      void queryClient.invalidateQueries({ queryKey: ['hr-employments'] });
      void queryClient.invalidateQueries({ queryKey: ['hr-movements-directory'] });
      onClose();
    },
    onError: (error) => {
      const message = getErrorMessage(error);
      setFormError(message);
      toast.error(message);
    },
  });

  const handleSubmit = (): void => {
    if (!joinedAt) return;
    const chosen = Object.fromEntries(
      (Object.keys(references) as ReferenceField[])
        .filter((field) => references[field] !== '')
        .map((field) => [field, references[field]])
    );
    start.mutate({
      joinedAt: new Date(joinedAt).toISOString(),
      status,
      workLocation,
      ...chosen,
      ...(managerId && { reportingManagerUserId: managerId }),
    });
  };

  const future = joinedAt !== '' && joinedAt > todayInputValue();

  return (
    <Modal open onClose={onClose} title={`Start employment · ${employeeName}`}>
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Creates this member&apos;s employment record for this company. Later changes to
          department, job, or manager go through movements.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            id="start-joined-at"
            label="Joining date"
            type="date"
            value={joinedAt}
            onChange={(event) => setJoinedAt(event.target.value)}
          />
          <Select
            id="start-status"
            label={future ? 'Status after joining' : 'Status'}
            value={status}
            clearable={false}
            onChange={(value) => setStatus(value === 'active' ? 'active' : 'probation')}
            options={[
              { value: 'probation', label: 'Probation' },
              { value: 'active', label: 'Active (confirmed)' },
            ]}
          />
        </div>
        {future && (
          <p className="text-xs text-gray-600 dark:text-gray-300">
            A future joining date starts in preboarding and switches on the joining date.
          </p>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            id="start-department"
            label="Department"
            value={references.departmentId}
            onChange={setReference('departmentId')}
            options={optionsFor('department', facets?.departments)}
          />
          <Select
            id="start-team"
            label="Team"
            value={references.teamId}
            onChange={setReference('teamId')}
            options={optionsFor('team', undefined, true)}
          />
          <Select
            id="start-job-title"
            label="Job title"
            value={references.jobTitleId}
            onChange={setReference('jobTitleId')}
            options={optionsFor('job_title', facets?.jobTitles)}
          />
          <Select
            id="start-position"
            label="Position"
            value={references.positionId}
            onChange={setReference('positionId')}
            options={optionsFor('position', undefined, true)}
          />
          <Select
            id="start-grade"
            label="Grade"
            value={references.gradeId}
            onChange={setReference('gradeId')}
            options={optionsFor('grade')}
          />
          <Select
            id="start-employment-type"
            label="Employment type"
            value={references.employmentTypeId}
            onChange={setReference('employmentTypeId')}
            options={optionsFor('employment_type', facets?.employmentTypes)}
          />
          <Select
            id="start-manager"
            label="Reporting manager"
            value={managerId}
            onChange={setManagerId}
            searchable
            options={[
              { value: '', label: 'Not set' },
              ...managerOptions,
            ]}
          />
          <Select
            id="start-work-location"
            label="Work location"
            value={workLocation}
            clearable={false}
            onChange={(value) =>
              setWorkLocation(value === 'remote' || value === 'hybrid' ? value : 'onsite')
            }
            options={[
              { value: 'onsite', label: 'On site' },
              { value: 'remote', label: 'Remote' },
              { value: 'hybrid', label: 'Hybrid' },
            ]}
          />
        </div>
        {formError && (
          <p className="text-sm text-danger" role="alert">
            {formError}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!joinedAt || start.isPending}
            loading={start.isPending}
            onClick={handleSubmit}
          >
            Start employment
          </Button>
        </div>
      </div>
    </Modal>
  );
}
